/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 */

import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@/components/link';
import logo from '/images/logo.svg';
import me4uImg from '/images/me4u.png';
import playStore from '/images/app-store-badge.svg';
import googlePlay from '/images/google-play-badge.svg';
import instaIcon from '/images/insta_icon.svg';
import xIcon from '/images/x_icon.svg';
import fbIcon from '/images/fb_icon.svg';
import {
    PhoneIcon,
    ArrowRightIcon,
} from '@/components/icons';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import AnimatedButton from '@/components/animated-button';

export default function MainFooter(): ReactElement {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleSubscribe = (e: FormEvent) => {
        e.preventDefault();

        if (email.trim()) {
            setSubscribed(true);

            setTimeout(() => {
                setDialogOpen(false);
                setSubscribed(false);
                setEmail('');
            }, 2000);
        }
    };

    return (
        <footer
            className="mt-auto border-border pb-6 md:pt-8 md:pb-13"
            style={{
                backgroundColor: '#F5F5F5',
                color: '#4D4D4D',
            }}
        >
            <div className="section-container min-[1400px]:!max-w-none ">
                {/* Top Section */}
                <div className="flex flex-col items-start justify-between gap-6 md:gap-10 ">
                    {/* Logo */}
                    <div className="shrink-0">
                        <Link to="/">
                            <img
                                src={logo}
                                alt="Mahindra Rise"
                                className="h-[50px] w-[131px]"
                            />
                        </Link>
                    </div>

                    {/* Footer Columns */}
                    <div className="flex w-full flex-col flex-wrap gap-8 md:flex-row md:justify-strech md:gap-12 lg:flex-nowrap lg:gap-16 xl:gap-150">
                 
                        {/* <div className="min-w-[200px] flex-1 space-y-3">
                            <p className="text-sm font-normal leading-tight text-[#4D4D4D]">
                                Register for the latest news and exclusive updates.
                            </p>

                            <Dialog
                                open={dialogOpen}
                                onOpenChange={setDialogOpen}
                            >
                                <DialogTrigger asChild>
                                    <AnimatedButton
                                        variant="primary"
                                        transparent
                                        icon={<ArrowRightIcon />}
                                    >
                                        Stay informed
                                    </AnimatedButton>
                                </DialogTrigger>

                                <DialogContent className="sm:max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>
                                            Stay informed
                                        </DialogTitle>

                                        <DialogDescription>
                                            Register for the latest news and
                                            exclusive updates from Mahindra.
                                        </DialogDescription>
                                    </DialogHeader>

                                    {subscribed ? (
                                        <div
                                            className="py-4 text-center font-semibold"
                                            style={{ color: '#16a34a' }}
                                        >
                                            Thank you for subscribing!
                                        </div>
                                    ) : (
                                        <form
                                            onSubmit={handleSubscribe}
                                            className="flex gap-2 pt-2"
                                        >
                                            <Input
                                                type="email"
                                                placeholder="Enter your email address"
                                                value={email}
                                                onChange={(e) =>
                                                    setEmail(e.target.value)
                                                }
                                                required
                                                className="flex-1"
                                            />

                                            <AnimatedButton
                                                type="submit"
                                                variant="secondary"
                                            >
                                                Subscribe
                                            </AnimatedButton>
                                        </form>
                                    )}
                                </DialogContent>
                            </Dialog>
                        </div> */}

                        {/* Assistance */}
                        <div className="min-w-[200px] space-y-3 pb-6 md:pb-2">
                            <p className="font-lato font-light text-[12px] leading-[20px] tracking-[0%] text-justify text-[#000000] md:text-[14px] md:text-left">
                                For any assistance call us on
                            </p>

                            <AnimatedButton
                                variant="primary"
                                transparent
                                component="a"
                                href="tel:18002096006"
                                icon={<PhoneIcon className="size-4" />}
                                hoverIcon={
                                    <ArrowRightIcon className="size-4" />
                                }
                                className="border border-black px-[20px] py-[11px] font-lato font-normal text-[12px] leading-[20px] tracking-[0%] md:text-[14px]"
                            >
                                1800 209 6006
                            </AnimatedButton>
                        </div>

                        {/* Desktop Social Links */}
                        <div className="hidden space-y-3 md:block ">
                            <p className="font-lato font-light text-[12px] leading-[20px] tracking-[0%] text-justify text-[#000000] md:text-[14px] md:text-left]">
                                Social links
                            </p>

                            <div className="flex items-center gap-4">
                                <a
                                    href="https://instagram.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex size-9 items-center justify-center"
                                >
                                    <img
                                        src={instaIcon}
                                        alt="Instagram"
                                        className="size-6"
                                    />
                                </a>

                                <a
                                    href="https://x.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex size-9 items-center justify-center"
                                >
                                    <img
                                        src={xIcon}
                                        alt="X"
                                        className="size-6"
                                    />
                                </a>

                                <a
                                    href="https://facebook.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex size-9 items-center justify-center"
                                >
                                    <img
                                        src={fbIcon}
                                        alt="Facebook"
                                        className="size-6"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Separator */}
                {/* <div className="my-8 mb-10 md:mb-8 border-t border-border" />

              
                <div className="my-6 flex flex-col-reverse gap-6 lg:grid lg:grid-cols-2">
          
                    <div className="flex min-h-[116px] flex-row items-center justify-center gap-5 rounded-[28px] bg-[#4D4D4D] px-[20px] md:px-7 py-[28px] md:py-8 text-white lg:min-h-0 lg:gap-4 lg:rounded-2xl lg:px-0 lg:py-0">
                        <div className="flex items-center gap-5 lg:gap-4">
          
                            <div className="flex size-[60px] md:size-[84px] shrink-0 items-center justify-center rounded-full bg-[#25D366] shadow-md lg:size-[80px]">
                                <svg
                                    className="size-[30px] md:size-[40px] md:size-11 fill-white"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.444 5.703 1.445h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </div>

                            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
                                <span className="whitespace-nowrap font-lato text-[16px] md:text-[20px] font-normal leading-[24px]">
                                    Let&apos;s connect on Whatsapp
                                </span>

                                <AnimatedButton
                                    component="a"
                                    href="https://wa.me/9118002096006"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="secondary"
                                    transparent
                                    icon={
                                        <ArrowRightIcon className="size-3 md:size-4" />
                                    }
                                    className="cursor-pointer whitespace-nowrap border-white text-[12px] md:text-[14px] text-white h-[28px] md:h-[42px]"
                                >
                                    Chat now
                                </AnimatedButton>
                            </div>
                        </div>
                    </div>

                 
                    <div className="relative flex min-h-[116px] md:min-h-[160px] items-center overflow-visible rounded-[28px] bg-[#9BB7EB] text-[#1A253C] lg:min-h-[148px] lg:rounded-2xl">
        
                        <img
                            src={me4uImg}
                            alt="M4U App"
                            className="absolute bottom-0 right-[16px] z-10 h-[178px] w-auto lg:left-[52px] lg:right-auto"
                        />

              
                        <div className="flex w-full flex-col items-start px-[20px] md:px-7 py-[20px] md:py-7 md:pr-[190px] lg:flex-row lg:items-center lg:gap-6 lg:px-6 lg:py-6 lg:pl-[190px] lg:pr-9">
                            <div className="shrink-0 font-lato text-[16px] md:text-[20px] font-normal leading-[26px] text-black">
                                <span className="block">
                                    Download the
                                </span>
                                <span className="block">
                                    M4U app now!
                                </span>
                            </div>

                            <div className="mt-4 flex items-center gap-2 lg:ml-auto lg:mt-0 lg:gap-3">
                                <a
                                    href="https://play.google.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 transition-opacity hover:opacity-90"
                                >
                                    <img
                                        src={googlePlay}
                                        alt="Get it on Google Play"
                                        className="h-[34px] w-[76px] md:w-auto lg:h-[42px]"
                                    />
                                </a>

                                <a
                                    href="https://apple.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 transition-opacity hover:opacity-90"
                                >
                                    <img
                                        src={playStore}
                                        alt="Download on the App Store"
                                        className="h-[34px] w-[76px] md:w-auto lg:h-[42px]"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* Desktop Separator */}
                <div className="my-8 hidden border-t border-border md:block" />

                {/* Mobile Social Links */}
                <div className="mb-8 flex items-center gap-6 md:hidden">
                    <a
                        href="https://instagram.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-8 items-center justify-center"
                    >
                        <img
                            src={instaIcon}
                            alt="Instagram"
                            className="size-7"
                        />
                    </a>

                    <a
                        href="https://x.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-8 items-center justify-center"
                    >
                        <img
                            src={xIcon}
                            alt="X"
                            className="size-7"
                        />
                    </a>

                    <a
                        href="https://facebook.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-8 items-center justify-center"
                    >
                        <img
                            src={fbIcon}
                            alt="Facebook"
                            className="size-7"
                        />
                    </a>
                </div>

                {/* Bottom Row */}
                <div className="flex flex-col items-start gap-8 font-lato font-normal text-[10px] leading-[16px] tracking-[0%] text-black sm:flex-row sm:items-center sm:justify-between sm:gap-4 md:text-[12px]">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <a
                            href="https://www.mahindra.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                        >
                            Mahindra Corporate
                        </a>

                        <Link
                            to="/terms-of-use"
                            className="hover:underline"
                        >
                            Terms &amp; Conditions
                        </Link>

                        <Link
                            to="/privacy-policy"
                            className="hover:underline"
                        >
                            Our Policy
                        </Link>
                    </div>

                    <div className='text-[#555555]'>
                        Copyright © 2021 Mahindra &amp; Mahindra Ltd
                    </div>
                </div>
            </div>
        </footer>
    );
}